/**
 * Upload a chat turn's composer files straight to storage, so `/api/chat`
 * carries artifact ids instead of bytes (unified file processing, PR 4b).
 *
 * Plain TS, no React and no hook callbacks: `useAgentChat` decides WHEN a
 * turn is sent and hands in its `abortRef`; this module picks the transport
 * for the turn's files, uploads them on the id path, and starts the POST. Kept
 * out of the hook so `useAgentChat.ts` stays under the file-size and
 * complexity ceilings.
 */
import { CAPTURE_SURFACE_AGENT_CHAT } from '@bower/shared/constants/capture';

import { ANALYTICS_EVENTS } from '../../constants/analytics';
import {
  sendAgentMessage,
  type ChatAttachmentRef,
  type ChatUploadedAttachments,
  type HistoryAttachment,
  type SendAgentMessageToolPicks,
  type SSEEventHandler,
  type WireContextItem,
} from '../../services/agentChat.service';
import {
  performResumableUpload,
  ResumableUploadAttemptError,
  type PerformUploadArgs,
} from '../../services/upload/performUpload';
import { safeAnalytics } from '../../utils/analytics';
import { getWorkspaceId } from '../../utils/workspaceState';

/** Why the turn's uploads fell short. Counts and bounded codes only, never a
 * file name: this feeds an analytics event. */
export interface ChatUploadFailure {
  readonly count: number;
  readonly stage: string;
  readonly errorCategory: string;
}

export type ChatUploadOutcome =
  | { readonly aborted: true }
  | {
      readonly aborted: false;
      readonly refs: readonly ChatAttachmentRef[];
      readonly failed: ChatUploadFailure | undefined;
      /** The files that failed, in input order: never stored, so a refused
       * send can hand them back (`refuseTurn`). */
      readonly failedFiles: readonly File[];
    };

function chatUploadArgs(workspaceId: string): PerformUploadArgs {
  return {
    workspaceId,
    parentEntityType: 'workspace',
    parentEntityId: workspaceId,
    uploadIntent: 'import',
    // Rides alongside the intent so a backend without `uploadIntent` still
    // imports, the same revert-safety the Import modal keeps (PR 4a).
    importAsNotes: true,
    captureSurface: CAPTURE_SURFACE_AGENT_CHAT,
    resumeAmbiguousFinalizeOnce: true,
  };
}

/** At most this many files upload at once. See `uploadChatAttachments`. */
const MAX_CONCURRENT_UPLOADS = 3;

/** One file's settled upload, kept at its input index. */
type UploadSettled =
  | { readonly ok: true; readonly ref: ChatAttachmentRef }
  | { readonly ok: false; readonly error: unknown };

async function settleOne(
  file: File,
  args: PerformUploadArgs,
  signal: AbortSignal,
): Promise<UploadSettled> {
  try {
    const { artifactId, intakeNote } = await performResumableUpload(file, args, undefined, signal);
    return { ok: true, ref: intakeNote ? { id: artifactId, intakeNote } : { id: artifactId } };
  } catch (error) {
    return { ok: false, error };
  }
}

/** The outcome from every file's result, read in input order. */
function summarize(settled: readonly UploadSettled[], files: readonly File[]): ChatUploadOutcome {
  const refs: ChatAttachmentRef[] = [];
  const failedFiles: File[] = [];
  let failedCount = 0;
  // The first TYPED failure (by input order) names the stage: a bare Error
  // carries no bounded code, so it only stands in when nothing typed failed.
  let firstTyped: ResumableUploadAttemptError | undefined;
  for (const [index, result] of settled.entries()) {
    if (result.ok) {
      refs.push(result.ref);
      continue;
    }
    failedCount += 1;
    failedFiles.push(files[index]);
    if (result.error instanceof ResumableUploadAttemptError) firstTyped ??= result.error;
  }
  if (failedCount === 0) return { aborted: false, refs, failed: undefined, failedFiles };
  const failed = firstTyped
    ? { count: failedCount, stage: firstTyped.stage, errorCategory: firstTyped.code }
    : { count: failedCount, stage: 'unknown', errorCategory: 'unexpected' };
  return { aborted: false, refs, failed, failedFiles };
}

/**
 * Upload every file. A failed file does not stop the rest (D1: the turn goes
 * out with what made it plus a failed count); an abort does: no new upload
 * starts, and then nothing about the turn may be sent.
 *
 * Up to three at once: each file pays a reserve and a finalize round trip
 * besides its bytes, and overlapping those hides most of that latency, while
 * the composer's ten files of up to 50 MB at once would split one uplink ten
 * ways and make every file finish late. Refs come back in the order the
 * researcher attached them, whatever order the uploads finish in.
 */
export async function uploadChatAttachments(
  files: readonly File[],
  workspaceId: string,
  signal: AbortSignal,
): Promise<ChatUploadOutcome> {
  const args = chatUploadArgs(workspaceId);
  const settled: UploadSettled[] = [];
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < files.length && !signal.aborted) {
      const index = next;
      next += 1;
      settled[index] = await settleOne(files[index], args, signal);
    }
  };
  const workers = Math.min(MAX_CONCURRENT_UPLOADS, files.length);
  await Promise.all(Array.from({ length: workers }, worker));
  if (signal.aborted) return { aborted: true };
  return summarize(settled, files);
}

/** How a turn's composer files travel: multipart bytes as before, artifact
 * ids after an upload, or not at all (the turn was canceled or left). */
export type ChatAttachmentPath =
  | { readonly kind: 'bytes' }
  | { readonly kind: 'aborted' }
  | {
      readonly kind: 'ids';
      readonly uploaded: ChatUploadedAttachments;
      /** The files that did not upload (`ChatUploadOutcome.failedFiles`). */
      readonly failedFiles: readonly File[];
      /** The controller the uploads ran on, still the turn's `abortRef`: the
       * POST rides it too, so one cancel covers uploads and request. */
      readonly controller: AbortController;
    };

const BYTES: ChatAttachmentPath = { kind: 'bytes' };
const ABORTED: ChatAttachmentPath = { kind: 'aborted' };

export interface ChooseChatAttachmentPathArgs {
  readonly files: readonly File[] | undefined;
  readonly isHitlReply: boolean;
  /** `useCanWrite()`: false while the role loads, so loading takes bytes. */
  readonly canWrite: boolean;
  readonly workspaceId: string;
  readonly sessionId: string;
  /** The hook's turn controller ref. Claimed BEFORE any await, so a cancel or
   * session switch during the uploads aborts this turn. */
  readonly abortRef: { current: AbortController | null };
}

/**
 * Pick the transport for this turn's files, uploading them when the id path
 * applies. Bytes stay the path for a text-only turn, a HITL reply and a
 * read-only (or still-loading) role. A demoted role that still reads as a
 * writer 403s at reserve and lands as a failed count (D1), never a broken turn.
 */
export async function chooseChatAttachmentPath(
  args: ChooseChatAttachmentPathArgs,
): Promise<ChatAttachmentPath> {
  const { files, workspaceId, sessionId, abortRef } = args;
  if (!files?.length || args.isHitlReply || !args.canWrite) return BYTES;
  const controller = new AbortController();
  abortRef.current = controller;
  const outcome = await uploadChatAttachments(files, workspaceId, controller.signal);
  if (outcome.aborted) return ABORTED;
  if (outcome.failed) {
    // Counts, the stage and a bounded code only: never a file name.
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_ATTACHMENTS_UPLOAD_FAILED, {
      session_id: sessionId,
      file_count: files.length,
      failed_count: outcome.failed.count,
      stage: outcome.failed.stage,
      error_category: outcome.failed.errorCategory,
    });
  }
  return {
    kind: 'ids',
    uploaded: { refs: outcome.refs, failedCount: outcome.failed?.count ?? 0 },
    failedFiles: outcome.failedFiles,
    controller,
  };
}

/** Everything `sendAgentMessage` needs for one turn. */
export interface ChatTurnPost {
  readonly text: string;
  readonly sessionId: string;
  /** The workspace the turn was sent in, captured at send. */
  readonly workspaceId: string;
  readonly onEvent: SSEEventHandler;
  readonly context: readonly WireContextItem[] | undefined;
  readonly files: File[] | undefined;
  readonly toolPicks: SendAgentMessageToolPicks;
}

/**
 * Start the POST for the turn's path, or `null` for a dropped turn (`aborted`,
 * or the workspace switched since send), which posts nothing to any session.
 * Bytes keep the six-argument multipart call; ids replace the files and ride
 * the uploads' controller.
 */
export function postChatTurn(
  path: ChatAttachmentPath,
  post: ChatTurnPost,
): Promise<AbortController> | null {
  if (path.kind === 'aborted') return null;
  // The request's `x-workspace-id` is read from the CURRENT workspace when it
  // is sent, and the uploads awaited first: a switch in
  // between would post this workspace's session and files under another's
  // header, and on the bytes path the agent would save them there. Dropped
  // like an aborted turn; the switch itself resets the chat. (No id at all
  // means no header, which the agent refuses, so only a real switch drops.)
  const current = getWorkspaceId();
  if (current !== null && current !== post.workspaceId) return null;
  const { text, sessionId, onEvent, context, toolPicks } = post;
  return path.kind === 'ids'
    ? sendAgentMessage(
        text,
        sessionId,
        onEvent,
        context,
        undefined,
        toolPicks,
        path.uploaded,
        path.controller,
      )
    : sendAgentMessage(text, sessionId, onEvent, context, post.files, toolPicks);
}

/** The optimistic user row's chips: by name, on either path. Lives here so
 * `sendMessage` stays under the complexity ceiling. */
export function composerChips(files: readonly File[] | undefined): HistoryAttachment[] | undefined {
  return files?.length ? files.map((f) => ({ name: f.name })) : undefined;
}
